import { useState } from 'react';

import config from '@/config';
import useGetSearchParams from '@/hooks/useGetSearchParams';
import useQuery from '@/hooks/useQuery';
import useStateHandler from '@/hooks/useStateHandler';
import { DropdownSort } from '@/shared-components/dropdown';
import SearchBarIcon from '@/shared-components/icons/components/forms/SearchBarIcon';
import Loader from '@/shared-components/loader/Loader';
import Text from '@/shared-components/text/components/Text';
import { canAccessGalleryPermission } from '@/utils/galleryPermissions';

import VirtualList from './virtual-list';

import './index.css';

export default function ArtistsAll() {
  const [params, setParams] = useGetSearchParams({
    validParams: ["q", "status"]
  });

  const [query, setQuery] = useState(
    params ? params : { q: "", status: "All" }
  );
  const { state } = useStateHandler();
  const session = state.user_session;
  const galleryId = session?.gallery_id || session?._id;
  const canViewArtists = canAccessGalleryPermission(session, 'artists.view_list', galleryId);

  const { loading, queryData, setQueryData, loadMoreData } = useQuery(
    "admin_artists_all",
    `${config.apis.api.url}/gallery/${galleryId}/artists/admin`,
    query,
    {
      injectToken: true,
      disabled: !canViewArtists,
      invalidateWhen: ["EDIT_ARTIST", "NEW_ARTIST"]
    }
  );

  if (!canViewArtists) {
    return <Text className="artists-management__empty-text font-size-small" paragraph>You do not have permission to view artists.</Text>;
  }

  function onInput(value) {
    const newQuery = {
      q: value,
      status: value ? "All" : query.status // si value vacío, mantén el status actual
    };
    setQuery(newQuery);
    setParams(newQuery, { replace: true });
  }

  function onStatusChange(status) {
    const newQuery = {
      status,
      q: query.q ? "" : query.q
    };
    setQuery(newQuery);
    setParams(newQuery, { replace: true });
  }

  return (
    <>
      <div className="artists-management__header">
        <div className="artists-management__search-bar-container">
          <SearchBarIcon className="artists-management__search-bar-icon" />
          <input
            type="text"
            className="artists-management__search-bar mt-m"
            value={query.q}
            onInput={(e) => onInput(e.target.value)}
            placeholder="Search artists..."
            spellCheck={false}
            autoComplete="off"
          />
        </div>
        <div className="artists-management__filter-popup">
          <DropdownSort
            defaultOption={query.status}
            options={["All", "Represented", "Created", "Claimed"]}
            onChangeOption={onStatusChange}
          />
        </div>
      </div>
      <div className="artists-management__list-container">
        {loading ? (
          <Loader />
        ) : queryData.data.data.length === 0 ? (
          <Text className="artists-management__empty-text font-size-small" paragraph>
            No artists found.
          </Text>
        ) : (
          <VirtualList
            key={queryData.queryString}
            fetchData={queryData.data}
            setFetchData={setQueryData}
            loadMoreData={loadMoreData}
          />
        )}
      </div>
    </>
  );
}
